import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Table, Badge, Alert, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './PaymentProviderManagement.css';
import './IntegrationSettings.css';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import CompanyProvisionService from '../../../../services/superadmin/CompanyProvisionService';
import PageTitle from '../../../layouts/PageTitle';
import CustomSelect from '../../../components/Forms/Element/CustomSelect';
import ModalPageEvent from '../../../components/Common/Modal/ModalPageEvent';
import { successAlert, errorAlert, warnAlertYesOrNo } from '../../../components/alert/defaultAlert';

// Guarda o job de sincronização em andamento (Celcoin/Asaas) no localStorage -
// sem isso, sair da tela no meio de uma sincronização (ela continua rodando no
// backend, é assíncrona de verdade) faz perder o acompanhamento do progresso para
// sempre: ao voltar, a tela não sabia que havia um job em andamento.
const PENDING_SYNC_JOB_KEY = 'payment_sync_job_pending';

const savePendingSyncJob = (job) => {
    localStorage.setItem(PENDING_SYNC_JOB_KEY, JSON.stringify(job));
};

const clearPendingSyncJob = () => {
    localStorage.removeItem(PENDING_SYNC_JOB_KEY);
};

const loadPendingSyncJob = () => {
    const stored = localStorage.getItem(PENDING_SYNC_JOB_KEY);
    if (!stored) return null;
    try {
        const job = JSON.parse(stored);
        return job?.jobId ? job : null;
    } catch {
        return null;
    }
};

const PaymentProviderManagement = () => {
    const { t } = useTranslation();
    
    // Estados principais
    const [currentProvider, setCurrentProvider] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [loading, setLoading] = useState(false);

    // Capabilities do provider (define quais opções de sync aparecem para o provider ativo)
    const [capabilities, setCapabilities] = useState({
        supportsPlans: false,
        supportsCustomers: false,
        supportsSubscriptions: false
    });

    const [asaasSyncResult, setAsaasSyncResult] = useState(null);
    const [asaasPreview, setAsaasPreview] = useState(null); // Preview (dry-run) antes de confirmar
    const [previewLoading, setPreviewLoading] = useState(false);
    const [showPreviewDetails, setShowPreviewDetails] = useState(false); // Expande as listas do preview
    // O que trazer na sincronização (útil no suporte). Padrão: tudo.
    const [syncScope, setSyncScope] = useState({ customers: true, plans: true, subscriptions: true });
    const [celcoinSyncResult, setCelcoinSyncResult] = useState(null);
    const [celcoinPreview, setCelcoinPreview] = useState(null); // Preview (dry-run) antes de confirmar

    // Job assíncrono da sincronização em andamento (Celcoin/Asaas, preview ou confirmação)
    // e controle do polling que acompanha o progresso - ver useEffect de polling mais abaixo.
    const [syncJob, setSyncJob] = useState(null);
    const [isPolling, setIsPolling] = useState(false);
    // jobId sendo cancelado no momento (trava o botão e evita clique duplo). Cobre tanto o
    // card do job ativo quanto as linhas do histórico.
    const [cancelingJobId, setCancelingJobId] = useState(null);

    const [lastSyncDates, setLastSyncDates] = useState({
        plans: null,
        customers: null,
        subscriptions: null
    });

    // Modal de confirmação
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    // Seleção de empresa (super admin — necessário para aplicar configurações por tenant)
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);

    // Histórico de sincronizações manuais + agendadas (SubscriptionChargeTask/
    // ProviderFullSyncTask) - dá visibilidade sobre o que o polling automático andou
    // fazendo, sem precisar entrar no log do servidor.
    const [jobHistory, setJobHistory] = useState([]);
    const [jobHistoryLoading, setJobHistoryLoading] = useState(false);

    // Credenciais Asaas inline
    const [asaasCredentials, setAsaasCredentials] = useState({ id: null, accessToken: '', sandbox: false, autoFullSyncEnabled: false });
    const [asaasCredentialsLoading, setAsaasCredentialsLoading] = useState(false);

    // Credenciais Celcoin (cel.cash) inline
    const [celcoinCredentials, setCelcoinCredentials] = useState({ id: null, galaxId: '', galaxHash: '', publicToken: '', sandbox: false, autoFullSyncEnabled: false });
    const [celcoinCredentialsLoading, setCelcoinCredentialsLoading] = useState(false);

    // Providers disponíveis
    const providers = [
        { value: 'ASAAS', label: 'Asaas', icon: 'fa-cloud-arrow-down', color: 'primary' },
        { value: 'CELCOIN', label: 'Celcoin', icon: 'fa-coins', color: 'warning' },
    ];

    // Obter provider selecionado como objeto para o CustomSelect
    const getSelectedProviderOption = () => {
        return providers.find(p => p.value === selectedProvider) || null;
    };

    // Carrega lista de empresas e datas de sync ao montar
    useEffect(() => {
        loadCompanies();
        loadLastSyncDates();
        loadJobHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recarrega o histórico filtrado quando a empresa selecionada muda (sem empresa
    // selecionada, mostra o histórico de todas).
    useEffect(() => {
        loadJobHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompanyId]);

    // Retoma o acompanhamento de um job que ficou em andamento quando a tela foi
    // desmontada (troca de tela) antes dele terminar - o job continua rodando no
    // backend independente disso, só o polling local que tinha parado.
    useEffect(() => {
        const pending = loadPendingSyncJob();
        if (!pending) return;

        if (pending.scope) {
            // O escopo pertence ao job já iniciado, não ao formulário recém-montado.
            // Restaurá-lo evita que um reload marque visualmente opções que não fazem
            // parte da sincronização em andamento.
            setSyncScope(pending.scope);
        }
        setSyncJob({
            jobId: pending.jobId,
            provider: pending.provider,
            dryRun: pending.dryRun,
            companyId: pending.companyId ?? null,
            scope: pending.scope ?? null,
            status: 'PENDING',
            currentStep: 'Retomando acompanhamento...',
            processedCustomers: 0,
            processedPlans: 0,
            processedSubscriptions: 0,
            errorMessage: null,
        });
        if (pending.companyId) {
            setSelectedCompanyId(pending.companyId);
        }
        if (pending.dryRun) {
            setPreviewLoading(true);
        } else {
            setLoading(true);
        }
        setIsPolling(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recarrega todas as configurações sempre que a empresa selecionada mudar
    useEffect(() => {
        if (!selectedCompanyId) return;

        // O job ativo (card de progresso + polling) é de uma sincronização específica de
        // uma empresa - se o usuário trocar de empresa no meio de uma sincronização, sem
        // isto o card/polling da empresa anterior continuava aparecendo na tela da nova
        // empresa selecionada, misturando o "em andamento" de uma com o histórico da outra.
        if (syncJob && syncJob.companyId && syncJob.companyId !== selectedCompanyId) {
            setIsPolling(false);
            setSyncJob(null);
            setLoading(false);
            setPreviewLoading(false);
        }

        setCurrentProvider(null);
        setAsaasSyncResult(null);
        setCelcoinSyncResult(null);
        setAsaasCredentials({ id: null, accessToken: '', sandbox: false, autoFullSyncEnabled: false });
        setCelcoinCredentials({ id: null, galaxId: '', galaxHash: '', publicToken: '', sandbox: false, autoFullSyncEnabled: false });
        loadCurrentProvider();
        loadAsaasSettings();
        loadCelcoinSettings();
    }, [selectedCompanyId]);

    useEffect(() => {
        if (currentProvider) {
            setSelectedProvider(currentProvider);
        }
    }, [currentProvider]);

    const loadLastSyncDates = () => {
        const stored = localStorage.getItem('payment_last_sync');
        if (stored) {
            setLastSyncDates(JSON.parse(stored));
        }
    };

    const saveFullSyncDate = () => {
        const dateStr = new Date().toISOString().split('T')[0];
        const updated = {
            plans: dateStr,
            customers: dateStr,
            subscriptions: dateStr
        };
        setLastSyncDates(updated);
        localStorage.setItem('payment_last_sync', JSON.stringify(updated));
    };

    const loadCompanies = async () => {
        setCompaniesLoading(true);
        try {
            const response = await CompanyProvisionService.pageable({ page: 0, size: 200 });
            const content = response.data?.content ?? response.data;
            const list = Array.isArray(content) ? content : [];
            setCompanies(list.map(c => ({ value: c.id, label: c.name })));
        } catch (error) {
            console.error('Erro ao carregar empresas:', error);
            errorAlert('Erro ao carregar empresas. Verifique o console.');
        } finally {
            setCompaniesLoading(false);
        }
    };

    const loadJobHistory = async () => {
        setJobHistoryLoading(true);
        try {
            const response = await axiosInstance.get(Api.integration.payment_sync_job, {
                params: { companyId: selectedCompanyId || undefined, limit: 50 }
            });
            setJobHistory(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Erro ao carregar histórico de sincronizações:', error);
        } finally {
            setJobHistoryLoading(false);
        }
    };

    const loadAsaasSettings = async () => {
        if (!selectedCompanyId) return;
        setAsaasCredentialsLoading(true);
        try {
            const response = await axiosInstance.get(`${Api.parameter.asaas_superadmin}/${selectedCompanyId}`);
            const d = response.data || {};
            setAsaasCredentials({ id: d.id || null, accessToken: d.accessToken || '', sandbox: Boolean(d.sandbox), autoFullSyncEnabled: Boolean(d.autoFullSyncEnabled) });
        } catch (error) {
            if (error?.response?.status !== 404) console.error('Erro ao carregar Asaas:', error);
        } finally {
            setAsaasCredentialsLoading(false);
        }
    };

    const loadCelcoinSettings = async () => {
        if (!selectedCompanyId) return;
        setCelcoinCredentialsLoading(true);
        try {
            const response = await axiosInstance.get(`${Api.parameter.celcoin_superadmin}/${selectedCompanyId}`);
            const d = response.data || {};
            setCelcoinCredentials({ id: d.id || null, galaxId: d.galaxId || '', galaxHash: d.galaxHash || '', publicToken: d.publicToken || '', sandbox: Boolean(d.sandbox), autoFullSyncEnabled: Boolean(d.autoFullSyncEnabled) });
        } catch (error) {
            if (error?.response?.status !== 404) console.error('Erro ao carregar Celcoin:', error);
        } finally {
            setCelcoinCredentialsLoading(false);
        }
    };

    const handleSaveAsaas = async () => {
        if (!asaasCredentials.accessToken?.trim()) {
            errorAlert('Preencha o Access Token do Asaas.');
            return;
        }
        setAsaasCredentialsLoading(true);
        try {
            const response = await axiosInstance.post(`${Api.parameter.asaas_superadmin}/${selectedCompanyId}`, { ...asaasCredentials, accessToken: asaasCredentials.accessToken.trim(), sandbox: Boolean(asaasCredentials.sandbox) });
            const d = response.data || {};
            setAsaasCredentials({ id: d.id || null, accessToken: d.accessToken || '', sandbox: Boolean(d.sandbox), autoFullSyncEnabled: Boolean(d.autoFullSyncEnabled) });
            successAlert('Configuração Asaas salva com sucesso!');
        } catch (error) {
            errorAlert(error?.response?.data?.message || 'Erro ao salvar configuração Asaas.');
        } finally {
            setAsaasCredentialsLoading(false);
        }
    };

    const handleSaveCelcoin = async () => {
        if (!celcoinCredentials.galaxId?.trim() || !celcoinCredentials.galaxHash?.trim()) {
            errorAlert('Preencha o Galax ID e o Galax Hash do Celcoin.');
            return;
        }
        setCelcoinCredentialsLoading(true);
        try {
            const response = await axiosInstance.post(`${Api.parameter.celcoin_superadmin}/${selectedCompanyId}`, { ...celcoinCredentials, galaxId: celcoinCredentials.galaxId.trim(), galaxHash: celcoinCredentials.galaxHash.trim(), publicToken: celcoinCredentials.publicToken?.trim() || null, sandbox: Boolean(celcoinCredentials.sandbox) });
            const d = response.data || {};
            setCelcoinCredentials({ id: d.id || null, galaxId: d.galaxId || '', galaxHash: d.galaxHash || '', publicToken: d.publicToken || '', sandbox: Boolean(d.sandbox), autoFullSyncEnabled: Boolean(d.autoFullSyncEnabled) });
            successAlert('Configuração Celcoin salva com sucesso!');
        } catch (error) {
            errorAlert(error?.response?.data?.message || 'Erro ao salvar configuração Celcoin.');
        } finally {
            setCelcoinCredentialsLoading(false);
        }
    };

    const loadCurrentProvider = async () => {
        if (!selectedCompanyId) return;
        setLoading(true);
        try {
            const tenantId = selectedCompanyId;
            const response = await axiosInstance.get(`${Api.integration.payment}/provider/${tenantId}`);
            const provider = response.data;
            setCurrentProvider(provider);
            
            // Carregar capabilities do provider
            await loadProviderCapabilities(provider);
        } catch (error) {
            console.error('Erro ao carregar provider atual:', error);
            errorAlert('Erro ao carregar configuração do provider');
        } finally {
            setLoading(false);
        }
    };

    // Espelha ProviderCapabilities do backend (PaymentController.getProviderCapabilities):
    // Celcoin e Asaas oferecem a sincronização completa de clientes, planos e assinaturas.
    const loadProviderCapabilities = async (provider) => {
        if (provider === 'ASAAS' || provider === 'CELCOIN') {
            setCapabilities({
                supportsPlans: true,
                supportsCustomers: true,
                supportsSubscriptions: true
            });
            return;
        }

        setCapabilities({
            supportsPlans: false,
            supportsCustomers: false,
            supportsSubscriptions: false
        });
    };

    const shouldShowAsaasSettings = currentProvider === 'ASAAS' || selectedProvider === 'ASAAS';
    const shouldShowCelcoinSettings = currentProvider === 'CELCOIN' || selectedProvider === 'CELCOIN';

    const handleSetProvider = () => {
        if (!selectedProvider) {
            errorAlert('Selecione um provider');
            return;
        }
        
        setConfirmAction({
            type: 'set_provider',
            title: 'Alterar Provider de Pagamento',
            message: `Tem certeza que deseja alterar o provider para ${getProviderLabel(selectedProvider)}?`,
            onConfirm: confirmSetProvider
        });
        setShowConfirmModal(true);
    };

    const confirmSetProvider = async () => {
        setLoading(true);
        try {
            const tenantId = selectedCompanyId;
            await axiosInstance.put(
                `${Api.integration.payment}/provider/${tenantId}`,
                null,
                { params: { provider: selectedProvider } }
            );
            
            setCurrentProvider(selectedProvider);
            successAlert('Provider configurado com sucesso!');

            // Recarregar capabilities do novo provider
            await loadProviderCapabilities(selectedProvider);
        } catch (error) {
            console.error('Erro ao configurar provider:', error);
            errorAlert('Erro ao configurar provider');
        } finally {
            setLoading(false);
            setShowConfirmModal(false);
        }
    };

    // Query string com o escopo escolhido (clientes/planos/assinaturas).
    const buildScopeQuery = () =>
        `customers=${syncScope.customers}&plans=${syncScope.plans}&subscriptions=${syncScope.subscriptions}`;
    const anyScopeSelected = syncScope.customers || syncScope.plans || syncScope.subscriptions;
    const toggleScope = (key) => setSyncScope(prev => ({ ...prev, [key]: !prev[key] }));
    const scopeLabels = (scope) => {
        if (!scope) return [];
        return [
            scope.customers && 'Clientes',
            scope.plans && 'Planos',
            scope.subscriptions && 'Assinaturas',
        ].filter(Boolean);
    };

    // Seletor do que trazer na sincronização. Assinaturas trazem junto o plano e o cliente
    // necessários — por isso a nota abaixo.
    const renderScopeSelector = () => (
        <div className="mb-3">
            <div className="d-flex align-items-center flex-wrap gap-3">
                <span className="text-muted"><small>Trazer:</small></span>
                <Form.Check inline type="checkbox" id="scope-customers" label="Clientes"
                    checked={syncScope.customers} onChange={() => toggleScope('customers')}
                    disabled={loading || previewLoading} />
                <Form.Check inline type="checkbox" id="scope-plans" label="Planos"
                    checked={syncScope.plans} onChange={() => toggleScope('plans')}
                    disabled={loading || previewLoading} />
                <Form.Check inline type="checkbox" id="scope-subscriptions" label="Assinaturas"
                    checked={syncScope.subscriptions} onChange={() => toggleScope('subscriptions')}
                    disabled={loading || previewLoading} />
            </div>
            {syncScope.subscriptions && (
                <small className="text-muted">Assinaturas trazem junto o plano e o cliente necessários.</small>
            )}
            {!anyScopeSelected && (
                <small className="text-danger d-block">Selecione ao menos uma opção.</small>
            )}
        </div>
    );

    const syncEndpointByProvider = {
        ASAAS: Api.integration.asaas_sync_superadmin,
        CELCOIN: Api.integration.celcoin_sync_superadmin,
    };

    const providerLabel = { ASAAS: 'Asaas', CELCOIN: 'Celcoin' };

    // Aplica o resultado de um job concluído no estado de preview/resultado do provider
    // correspondente - mesmo shape que os handlers antigos já usavam.
    const applySyncResult = (provider, dryRun, result) => {
        const safeResult = result || {};
        if (provider === 'ASAAS') {
            if (dryRun) {
                setAsaasPreview(safeResult);
            } else {
                setAsaasSyncResult(safeResult);
                setAsaasPreview(null);
                saveFullSyncDate();
                successAlert('Sincronização do Asaas concluída.');
            }
        } else if (provider === 'CELCOIN') {
            if (dryRun) {
                setCelcoinPreview(safeResult);
            } else {
                setCelcoinSyncResult(safeResult);
                setCelcoinPreview(null);
                saveFullSyncDate();
                successAlert('Sincronização do Celcoin concluída.');
            }
        }
    };

    // Dispara a sincronização (preview ou confirmação) de forma assíncrona: o backend cria
    // um job e devolve o jobId na hora; o progresso é acompanhado por polling (useEffect
    // abaixo), igual ao padrão já usado na importação do Cashbarber.
    const startSync = async (provider, dryRun) => {
        if (currentProvider !== provider) {
            errorAlert(`A sincronização ${providerLabel[provider]} só está disponível quando o provider ativo é ${providerLabel[provider]}.`);
            return;
        }

        if (dryRun) {
            setPreviewLoading(true);
            setShowPreviewDetails(false);
        } else {
            setLoading(true);
        }

        try {
            const endpoint = syncEndpointByProvider[provider];
            const requestedScope = { ...syncScope };
            const scopeQuery = `&${buildScopeQuery()}`;
            const response = await axiosInstance.post(`${endpoint}/${selectedCompanyId}/async?dryRun=${dryRun}${scopeQuery}`);

            savePendingSyncJob({
                jobId: response.data.jobId,
                provider,
                dryRun,
                companyId: selectedCompanyId,
                scope: requestedScope,
            });
            setSyncJob({
                jobId: response.data.jobId,
                provider,
                dryRun,
                companyId: selectedCompanyId,
                scope: requestedScope,
                status: 'PENDING',
                currentStep: 'Iniciando...',
                processedCustomers: 0,
                processedPlans: 0,
                processedSubscriptions: 0,
                errorMessage: null,
            });
            setIsPolling(true);
        } catch (error) {
            console.error(`Erro ao iniciar sincronização ${provider}:`, error);
            if (dryRun) {
                setPreviewLoading(false);
            } else {
                setLoading(false);
            }
            // Backend recusou porque já existe uma sincronização rodando para este
            // tenant+provedor: em vez de só mostrar o erro e deixar o usuário sem saída,
            // recupera o job ativo para exibir o progresso dele e o botão de cancelar.
            const resumed = await resumeActiveJob(provider);
            if (!resumed) {
                errorAlert(error?.response?.data?.message || error);
            }
        }
    };

    // Busca o job em andamento (PENDING/RUNNING) do tenant+provedor e passa a acompanhá-lo,
    // para o usuário conseguir ver/cancelar a sincronização que está bloqueando. Retorna
    // true se encontrou algo para acompanhar.
    const resumeActiveJob = async (provider) => {
        try {
            const res = await axiosInstance.get(`${Api.integration.payment_sync_job}/active`, {
                params: { companyId: selectedCompanyId, provider },
            });
            if (!res.data || !res.data.jobId) {
                return false;
            }
            const data = res.data;
            savePendingSyncJob({ jobId: data.jobId, provider, dryRun: false, companyId: selectedCompanyId });
            setSyncJob({ ...data, provider, dryRun: false, companyId: selectedCompanyId });
            setIsPolling(true);
            return true;
        } catch (error) {
            // 204 (sem job ativo) cai aqui como resposta vazia - nada a retomar.
            return false;
        }
    };

    // Confirmação antes de cancelar (padrão do projeto: warnAlertYesOrNo, já temático/dark) -
    // cancelar aborta a sincronização em andamento, então evitamos clique acidental no card
    // ou numa linha do histórico.
    const requestCancelSyncJob = (jobId) => {
        if (!jobId) return;
        warnAlertYesOrNo(
            'Deseja cancelar esta sincronização em andamento? O que já foi importado até aqui é mantido; o restante não será processado.',
            () => performCancelSyncJob(jobId));
    };

    // Cancela um job em andamento. A sincronização interrompe na próxima fronteira de
    // página (checagem cooperativa no backend); aqui só paramos o acompanhamento local.
    const performCancelSyncJob = async (jobId) => {
        if (!jobId) return;
        setCancelingJobId(jobId);
        try {
            await axiosInstance.delete(`${Api.integration.payment_sync_job}/${jobId}`);
            successAlert('Sincronização cancelada.');
            if (syncJob?.jobId === jobId) {
                setIsPolling(false);
                setSyncJob(prev => (prev ? { ...prev, status: 'CANCELED', currentStep: 'Cancelado pelo usuário' } : prev));
                clearPendingSyncJob();
                setLoading(false);
                setPreviewLoading(false);
            }
            loadJobHistory();
        } catch (error) {
            console.error('Erro ao cancelar sincronização:', error);
            errorAlert(error?.response?.data?.message || 'Erro ao cancelar a sincronização.');
        } finally {
            setCancelingJobId(null);
        }
    };

    // Polling do progresso: consulta a cada 3s até o job terminar (mesmo intervalo usado
    // na importação do Cashbarber).
    useEffect(() => {
        let interval;
        if (isPolling && syncJob?.jobId) {
            interval = setInterval(async () => {
                try {
                    const res = await axiosInstance.get(`${Api.integration.payment_sync_job}/${syncJob.jobId}`);
                    const data = res.data;
                    setSyncJob(prev => (prev ? { ...prev, ...data } : prev));

                    if (data.status === 'COMPLETED' || data.status === 'FAILED' || data.status === 'CANCELED') {
                        setIsPolling(false);
                        clearPendingSyncJob();
                        if (syncJob.dryRun) {
                            setPreviewLoading(false);
                        } else {
                            setLoading(false);
                        }

                        if (data.status === 'COMPLETED') {
                            applySyncResult(syncJob.provider, syncJob.dryRun, data.result);
                        } else if (data.status === 'FAILED') {
                            errorAlert(data.errorMessage || `Erro ao sincronizar ${providerLabel[syncJob.provider]}.`);
                        }
                        // CANCELED: cancelamento já é sinalizado por successAlert em cancelSyncJob.
                        loadJobHistory();
                    }
                } catch (error) {
                    console.error('Erro ao consultar progresso da sincronização:', error);
                    errorAlert(error?.response?.data?.message || error);
                    setIsPolling(false);
                    // Job nao encontrado (ex: id invalido apos algum reset) - nao ha o que
                    // retomar depois, entao para de tentar de novo a cada remontagem da tela.
                    if (error?.response?.status === 404 || error?.response?.status === 400) {
                        clearPendingSyncJob();
                    }
                    if (syncJob.dryRun) {
                        setPreviewLoading(false);
                    } else {
                        setLoading(false);
                    }
                }
            }, 3000);
        }
        return () => { if (interval) clearInterval(interval); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPolling, syncJob?.jobId]);

    // Enquanto houver QUALQUER job ativo no histórico (inclusive os agendados, que não têm
    // polling próprio), recarrega a lista a cada 4s para o progresso ao vivo e as barras
    // avançarem na tabela. Para de sozinho quando nenhum job está mais em andamento.
    const hasActiveJobInHistory = jobHistory.some(job => job.status === 'PENDING' || job.status === 'RUNNING');
    useEffect(() => {
        if (!hasActiveJobInHistory) return undefined;
        const interval = setInterval(() => { loadJobHistory(); }, 4000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasActiveJobInHistory]);

    const handlePreviewAsaas = () => startSync('ASAAS', true);
    const handleSyncAsaas = () => startSync('ASAAS', false);
    const handlePreviewCelcoin = () => startSync('CELCOIN', true);
    const handleSyncCelcoin = () => startSync('CELCOIN', false);

    // Percentual crescente por FASE (a descoberta é paginada, o backend nem sempre sabe o
    // total antecipadamente). Cada etapa tem um piso fixo e cresce um pouco conforme os
    // contadores avançam, sem nunca alcançar o piso da próxima etapa - assim a barra sempre
    // se move para frente. `currentStep` vem do backend como texto ("Sincronizando
    // clientes...", "...assinaturas...", "...planos...", "Concluído"); casamos por trecho
    // para não depender da frase exata.
    const computeSyncProgress = (job) => {
        if (!job) return 0;
        if (job.status === 'COMPLETED') return 100;
        const step = (job.currentStep || '').toLowerCase();
        const processed = (job.processedCustomers || 0) + (job.processedSubscriptions || 0) + (job.processedPlans || 0);
        // Empurrãozinho intra-fase: satura rápido (log) para a barra andar nos primeiros
        // registros sem estourar o piso da próxima fase (cada fase tem ~22% de largura).
        const bump = Math.min(18, Math.round(Math.log10(processed + 1) * 9));
        if (step.includes('conclu')) return 100;
        if (step.includes('plano')) return 70 + bump;      // 70–88%
        if (step.includes('assinatura')) return 45 + bump; // 45–63%
        if (step.includes('cliente')) return 20 + bump;    // 20–38%
        return 8; // PENDING / "Iniciando..."
    };

    // Barra de progresso exibida no card do provider que está sincronizando no momento.
    const renderSyncProgress = (provider) => {
        const active = syncJob && syncJob.provider === provider
            && (isPolling || syncJob.status === 'FAILED' || syncJob.status === 'CANCELED');
        if (!active) {
            return null;
        }

        const isFailed = syncJob.status === 'FAILED';
        const isCanceled = syncJob.status === 'CANCELED';
        const isTerminal = isFailed || isCanceled;
        const pct = isFailed || isCanceled ? 100 : computeSyncProgress(syncJob);
        const variant = isFailed ? 'danger' : isCanceled ? 'warning' : 'primary';
        const canCancel = !isTerminal && syncJob.jobId;
        const runningScopeLabels = scopeLabels(syncJob.scope);
        return (
            <div className="mb-3">
                <ProgressBar
                    now={pct}
                    label={isTerminal ? undefined : `${pct}%`}
                    animated={!isTerminal}
                    striped
                    variant={variant}
                />
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="text-muted small">
                        {!isTerminal && <i className="fas fa-spinner fa-spin me-2"></i>}
                        {syncJob.currentStep} — {syncJob.processedCustomers ?? 0} clientes, {syncJob.processedSubscriptions ?? 0} assinaturas, {syncJob.processedPlans ?? 0} planos
                        {runningScopeLabels.length > 0 && (
                            <span className="d-block mt-1">
                                Executando: <strong>{runningScopeLabels.join(', ')}</strong>
                            </span>
                        )}
                        {(syncJob.startedAt || syncJob.createdAt) && (
                            <span className="d-block mt-1">
                                Tempo decorrido: <strong>{formatJobDuration(syncJob)}</strong>
                            </span>
                        )}
                    </div>
                    {canCancel && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            className="ms-2 flex-shrink-0"
                            onClick={() => requestCancelSyncJob(syncJob.jobId)}
                            disabled={cancelingJobId === syncJob.jobId}
                        >
                            {cancelingJobId === syncJob.jobId ? (
                                <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Cancelando...</>
                            ) : (
                                <><i className="fas fa-stop-circle me-1"></i>Cancelar</>
                            )}
                        </Button>
                    )}
                </div>
                {isFailed && syncJob.errorMessage && (
                    <div className="alert alert-danger mt-2 mb-0">{syncJob.errorMessage}</div>
                )}
                {isCanceled && (
                    <div className="alert alert-warning mt-2 mb-0">Sincronização cancelada.</div>
                )}
            </div>
        );
    };

    // Renderiza as listas detalhadas do preview (dry-run): o que será criado/atualizado.
    // idKey diferencia Asaas ('asaasId') de Celcoin ('celcoinId').
    const renderPreviewData = (preview, idKey) => {
        const sections = [
            { title: 'Clientes', items: preview.customers || [] },
            { title: 'Assinaturas', items: preview.subscriptions || [] },
            { title: 'Planos', items: preview.plans || [] },
        ];
        return (
            <div className="mt-3">
                {sections.map((sec) => {
                    const creates = sec.items.filter(i => i.action === 'CREATE');
                    const updates = sec.items.filter(i => i.action === 'UPDATE');
                    // Criações primeiro (o que interessa conferir), depois atualizações.
                    const ordered = [...creates, ...updates];
                    if (ordered.length === 0) return null;
                    return (
                        <div key={sec.title} className="mb-4 payment-mgmt__preview-section">
                            <div className="payment-mgmt__preview-header">
                                <div>
                                    <span className="payment-mgmt__preview-title">{sec.title}</span>
                                    <span className="payment-mgmt__preview-total">
                                        {ordered.length} registro(s)
                                    </span>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Badge bg="success">{creates.length} a criar</Badge>
                                    <Badge bg="secondary">{updates.length} a atualizar</Badge>
                                </div>
                            </div>
                            <div className="payment-mgmt__job-history-scroll payment-mgmt__preview-scroll">
                                <Table responsive className="payment-mgmt__job-history-table payment-mgmt__preview-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 110 }}>Ação</th>
                                            <th>Nome</th>
                                            <th>Identificador externo</th>
                                            <th>Correspondência</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ordered.map((it, i) => (
                                            <tr
                                                key={`${it[idKey] || 'item'}-${i}`}
                                                className={it.action === 'CREATE' ? 'payment-mgmt__preview-row-create' : undefined}
                                            >
                                                <td>
                                                    <Badge bg={it.action === 'CREATE' ? 'success' : 'secondary'}>
                                                        <i className={`fas ${it.action === 'CREATE' ? 'fa-plus' : 'fa-rotate'} me-1`}></i>
                                                        {it.action === 'CREATE' ? 'Criar' : 'Atualizar'}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <span className="fw-semibold">{it.name || 'Sem nome'}</span>
                                                    {it.possibleDuplicate && (
                                                        <Badge bg="warning" text="dark" className="ms-2">
                                                            <i className="fas fa-triangle-exclamation me-1"></i>
                                                            Possível duplicata
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <code className="payment-mgmt__inline-code">{it[idKey] || '-'}</code>
                                                </td>
                                                <td>
                                                    <span className="text-muted">{it.matchedBy || 'Não identificada'}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const jobTypeLabel = { FULL_SYNC: 'Descoberta completa', CHARGE_RECONCILIATION: 'Reconciliação de cobrança' };
    const jobTriggerLabel = { MANUAL: 'Manual', SCHEDULED: 'Agendado' };
    const jobStatusVariant = { PENDING: 'secondary', RUNNING: 'primary', COMPLETED: 'success', FAILED: 'danger', CANCELED: 'warning' };
    const jobStatusLabel = { PENDING: 'Pendente', RUNNING: 'Em andamento', COMPLETED: 'Concluído', FAILED: 'Falhou', CANCELED: 'Cancelado' };
    const isJobActive = (status) => status === 'PENDING' || status === 'RUNNING';

    const formatJobDate = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleString('pt-BR');
    };

    // Complementa a data absoluta com um texto relativo curto ("há 5 min"), sem
    // depender de nenhuma lib extra de formatação de datas.
    const formatRelativeJobDate = (value) => {
        if (!value) return '';
        const diffMs = Date.now() - new Date(value).getTime();
        const diffMin = Math.round(diffMs / 60000);
        if (diffMin < 1) return 'agora mesmo';
        if (diffMin < 60) return `há ${diffMin} min`;
        const diffHours = Math.round(diffMin / 60);
        if (diffHours < 24) return `há ${diffHours}h`;
        const diffDays = Math.round(diffHours / 24);
        return `há ${diffDays}d`;
    };

    const formatJobDuration = (job) => {
        const startValue = job?.startedAt || job?.createdAt;
        if (!startValue) return '-';
        const terminal = job.status === 'COMPLETED' || job.status === 'FAILED' || job.status === 'CANCELED';
        const endValue = job.finishedAt || (terminal ? job.updatedAt : null);
        const elapsedSeconds = Math.max(0, Math.floor(
            ((endValue ? new Date(endValue).getTime() : Date.now()) - new Date(startValue).getTime()) / 1000
        ));
        const hours = Math.floor(elapsedSeconds / 3600);
        const minutes = Math.floor((elapsedSeconds % 3600) / 60);
        const seconds = elapsedSeconds % 60;
        if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}min`;
        if (minutes > 0) return `${minutes}min ${String(seconds).padStart(2, '0')}s`;
        return `${seconds}s`;
    };

    // Resume o resultado de um job pra uma linha curta na tabela - o shape de `result`
    // varia por jobType (descoberta completa traz customersCreated/plansCreated/etc,
    // reconciliação de cobrança traz subscriptionsChecked/chargesUpdated/failures).
    const summarizeJobResult = (job) => {
        if (job.status === 'FAILED') {
            return job.errorMessage || 'Erro não informado';
        }
        if (job.status !== 'COMPLETED' || !job.result) {
            return job.currentStep || '-';
        }
        if (job.jobType === 'CHARGE_RECONCILIATION') {
            const r = job.result;
            return `${r.subscriptionsChecked ?? 0} assinatura(s) verificada(s), ${r.chargesUpdated ?? 0} cobrança(s) atualizada(s)${r.failures ? `, ${r.failures} falha(s)` : ''}`;
        }
        const r = job.result;
        const parts = [];
        if (r.customersCreated != null) parts.push(`${r.customersCreated} cliente(s) criado(s)`);
        if (r.plansCreated != null) parts.push(`${r.plansCreated} plano(s) criado(s)`);
        if (r.subscriptionsCreated != null) parts.push(`${r.subscriptionsCreated} assinatura(s) criada(s)`);
        return parts.length > 0 ? parts.join(', ') : 'Concluído';
    };

    const renderJobHistory = () => (
        <Row className="mb-4">
            <Col xl={12}>
                <Card className="integration-settings__card">
                    <Card.Header className="integration-settings__header d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-0">
                                <i className="fas fa-history me-2"></i>
                                Histórico de sincronizações
                            </h5>
                            <p className="mb-0 mt-1 text-muted" style={{ fontSize: '0.875rem' }}>
                                Manuais e agendadas (reconciliação de cobrança de hora em hora, descoberta completa a cada 6h).
                            </p>
                        </div>
                        <Button variant="outline-secondary" size="sm" onClick={loadJobHistory} disabled={jobHistoryLoading}>
                            <i className={`fas fa-sync-alt ${jobHistoryLoading ? 'fa-spin' : ''} me-1`}></i>
                            Atualizar
                        </Button>
                    </Card.Header>
                    <Card.Body className="integration-settings__body">
                        {jobHistoryLoading && jobHistory.length === 0 ? (
                            <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                            </div>
                        ) : jobHistory.length === 0 ? (
                            <div className="payment-mgmt__job-history-empty">
                                <i className="fas fa-clock-rotate-left"></i>
                                <p className="mb-0">Nenhuma sincronização registrada ainda.</p>
                            </div>
                        ) : (
                            <>
                                <div className="payment-mgmt__job-history-scroll">
                                    <Table className="mb-0 payment-mgmt__job-history-table">
                                        <thead>
                                            <tr>
                                                <th>Quando</th>
                                                <th>Empresa</th>
                                                <th>Provedor</th>
                                                <th>Tipo</th>
                                                <th>Origem</th>
                                                <th>Status</th>
                                                <th>Tempo</th>
                                                <th>Resultado / Progresso</th>
                                                <th className="text-end">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {jobHistory.map((job) => {
                                                const providerInfo = getProviderInfo(job.provider);
                                                const active = isJobActive(job.status);
                                                const pct = computeSyncProgress(job);
                                                return (
                                                    <tr key={job.jobId} className={active ? 'payment-mgmt__job-row-active' : undefined}>
                                                        <td className="text-nowrap" title={formatJobDate(job.createdAt)}>
                                                            <div>{formatJobDate(job.createdAt)}</div>
                                                            <small className="text-muted">{formatRelativeJobDate(job.createdAt)}</small>
                                                        </td>
                                                        <td>{job.companyName || (job.companyId ? `Empresa #${job.companyId}` : '-')}</td>
                                                        <td>
                                                            <span className="payment-mgmt__provider-tag">
                                                                {providerInfo.icon && <i className={`fas ${providerInfo.icon}`}></i>}
                                                                {providerLabel[job.provider] || job.provider}
                                                            </span>
                                                        </td>
                                                        <td>{jobTypeLabel[job.jobType] || '-'}</td>
                                                        <td>
                                                            <Badge pill bg={job.trigger === 'SCHEDULED' ? 'info' : 'secondary'}>
                                                                {jobTriggerLabel[job.trigger] || '-'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge pill bg={jobStatusVariant[job.status] || 'secondary'}>
                                                                {active && <i className="fas fa-spinner fa-spin me-1"></i>}
                                                                {jobStatusLabel[job.status] || job.status}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-nowrap">
                                                            <i className="far fa-clock me-1 text-muted"></i>
                                                            {formatJobDuration(job)}
                                                        </td>
                                                        <td className="payment-mgmt__job-history-result">
                                                            {active ? (
                                                                <div style={{ minWidth: 180 }}>
                                                                    <ProgressBar now={pct} label={`${pct}%`} animated striped variant="primary" style={{ height: 16 }} />
                                                                    <small className="text-muted d-block mt-1">
                                                                        {job.currentStep || 'Iniciando...'} — {job.processedCustomers ?? 0}c / {job.processedSubscriptions ?? 0}a / {job.processedPlans ?? 0}p
                                                                    </small>
                                                                </div>
                                                            ) : (
                                                                <span title={summarizeJobResult(job)}>{summarizeJobResult(job)}</span>
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                            {active && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => requestCancelSyncJob(job.jobId)}
                                                                    disabled={cancelingJobId === job.jobId}
                                                                    title="Cancelar esta sincronização"
                                                                >
                                                                    {cancelingJobId === job.jobId
                                                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                        : <i className="fas fa-stop-circle"></i>}
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="payment-mgmt__job-history-caption mb-0">
                                    Mostrando os últimos {jobHistory.length} registro{jobHistory.length === 1 ? '' : 's'}.
                                </p>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    const getProviderInfo = (providerValue) => {
        return providers.find(p => p.value === providerValue) || {};
    };

    const getProviderLabel = (providerValue) => {
        const info = getProviderInfo(providerValue);
        return info.label || providerValue;
    };

    const renderOverview = () => (
        <Row className="payment-mgmt__overview">
            {/* Provider Atual */}
            <Col md={6} className="mb-4">
                <Card className="integration-settings__card payment-mgmt__provider-card h-100">
                    <Card.Header className="integration-settings__header">
                        <div>
                            <h5>
                                <i className="fas fa-cog me-2"></i>
                                Provider Configurado
                            </h5>
                        </div>
                    </Card.Header>
                    <Card.Body className="integration-settings__body">
                        {currentProvider ? (
                            <div className="payment-mgmt__provider-state">
                                <div className="integration-settings__status-icon payment-mgmt__provider-icon mx-auto mb-3">
                                    <i className={`fas ${getProviderInfo(currentProvider).icon}`}></i>
                                </div>
                                <h4>{getProviderLabel(currentProvider)}</h4>
                                <p className="payment-mgmt__provider-copy">Provider usado nas rotinas de cobrança, importação e acompanhamento de assinaturas.</p>
                                <Badge bg="info" className="mt-2">
                                    <i className="fas fa-check-circle me-1"></i>
                                    Autenticação automática
                                </Badge>

                                {/* Capabilities */}
                                <div className="mt-4 pt-4 border-top">
                                    <small className="text-muted d-block mb-3">
                                        <strong>Funcionalidades Disponíveis:</strong>
                                    </small>
                                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                                        <Badge 
                                            bg={capabilities.supportsPlans ? 'success' : 'secondary'} 
                                            pill
                                            className="py-2 px-3"
                                        >
                                            <i className={`fas fa-${capabilities.supportsPlans ? 'check' : 'times'} me-1`}></i>
                                            Planos
                                        </Badge>
                                        <Badge 
                                            bg={capabilities.supportsCustomers ? 'success' : 'secondary'} 
                                            pill
                                            className="py-2 px-3"
                                        >
                                            <i className={`fas fa-${capabilities.supportsCustomers ? 'check' : 'times'} me-1`}></i>
                                            Clientes
                                        </Badge>
                                        <Badge 
                                            bg={capabilities.supportsSubscriptions ? 'success' : 'secondary'} 
                                            pill
                                            className="py-2 px-3"
                                        >
                                            <i className={`fas fa-${capabilities.supportsSubscriptions ? 'check' : 'times'} me-1`}></i>
                                            Assinaturas
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
                                <p>Nenhum provider configurado</p>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            {/* Seletor de Provider */}
            <Col md={6} className="mb-4">
                <Card className="integration-settings__card payment-mgmt__switch-card h-100">
                    <Card.Header className="integration-settings__header">
                        <div>
                            <h5>
                                <i className="fas fa-exchange-alt me-2"></i>
                                Alterar Provider
                            </h5>
                        </div>
                    </Card.Header>
                    <Card.Body className="integration-settings__body">
                        <Form.Group className="mb-3">
                            <Form.Label>Selecione o Provider</Form.Label>
                            <CustomSelect
                                value={getSelectedProviderOption()}
                                onChange={(option) => setSelectedProvider(option?.value || '')}
                                options={providers}
                                placeholder="Selecione..."
                                isClearable={true}
                                isDisabled={loading}
                            />
                        </Form.Group>

                        <div className="d-grid gap-2">
                            <Button
                                variant="primary"
                                onClick={handleSetProvider}
                                disabled={loading || !selectedProvider || selectedProvider === currentProvider}
                            >
                                <i className="fas fa-check me-2"></i>
                                Aplicar provider
                            </Button>
                        </div>

                        {selectedProvider && selectedProvider !== currentProvider && (
                            <Alert variant="warning" className="mt-3 mb-0">
                                <small>
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Ao alterar o provider, você precisará reautenticar e sincronizar os dados.
                                </small>
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            {shouldShowAsaasSettings && (
                <Col md={12} className="mt-4">
                    <Card className="integration-settings__card">
                        <Card.Header className="integration-settings__header">
                            <div>
                                <h5><i className="fas fa-cloud-arrow-down me-2"></i>Credenciais Asaas</h5>
                            </div>
                        </Card.Header>
                        <Card.Body className="integration-settings__body">
                            {asaasCredentialsLoading ? (
                                <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" role="status" /></div>
                            ) : (
                                <>
                                    <Row>
                                        <Col md={8} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Access Token</Form.Label>
                                                <Form.Control type="password" value={asaasCredentials.accessToken} onChange={e => setAsaasCredentials(p => ({ ...p, accessToken: e.target.value }))} disabled={asaasCredentialsLoading} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Ambiente</Form.Label>
                                                <div className="integration-settings__switch-field">
                                                    <Form.Check type="switch" id="asaas-sandbox-inline" label="Sandbox" checked={asaasCredentials.sandbox} onChange={e => setAsaasCredentials(p => ({ ...p, sandbox: e.target.checked }))} disabled={asaasCredentialsLoading} />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md={12} className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="asaas-auto-full-sync"
                                                label="Sincronização automática (descoberta de clientes/planos/assinaturas, de hora em hora)"
                                                checked={asaasCredentials.autoFullSyncEnabled}
                                                onChange={e => setAsaasCredentials(p => ({ ...p, autoFullSyncEnabled: e.target.checked }))}
                                                disabled={asaasCredentialsLoading}
                                            />
                                        </Col>
                                    </Row>
                                    <div className="d-flex justify-content-end">
                                        <Button variant="primary" onClick={handleSaveAsaas} disabled={asaasCredentialsLoading}>
                                            <i className="fas fa-save me-1"></i>Salvar Asaas
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            )}

            {shouldShowCelcoinSettings && (
                <Col md={12} className="mt-4">
                    <Card className="integration-settings__card">
                        <Card.Header className="integration-settings__header">
                            <div>
                                <h5><i className="fas fa-coins me-2"></i>Credenciais Celcoin</h5>
                            </div>
                        </Card.Header>
                        <Card.Body className="integration-settings__body">
                            {celcoinCredentialsLoading ? (
                                <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" role="status" /></div>
                            ) : (
                                <>
                                    <Alert variant="info">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Informe o Galax ID e o Galax Hash da conta cel.cash (Celcoin). Configure o webhook em Webservice → Webhook apontando para <code className="payment-mgmt__inline-code">/api/public/payments/celcoin/&#123;companyUuid&#125;</code>.
                                    </Alert>
                                    <Row>
                                        <Col md={6} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Galax ID</Form.Label>
                                                <Form.Control type="text" value={celcoinCredentials.galaxId} onChange={e => setCelcoinCredentials(p => ({ ...p, galaxId: e.target.value }))} disabled={celcoinCredentialsLoading} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Galax Hash</Form.Label>
                                                <Form.Control type="password" value={celcoinCredentials.galaxHash} onChange={e => setCelcoinCredentials(p => ({ ...p, galaxHash: e.target.value }))} disabled={celcoinCredentialsLoading} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>Token público</Form.Label>
                                                <Form.Control type="text" value={celcoinCredentials.publicToken} onChange={e => setCelcoinCredentials(p => ({ ...p, publicToken: e.target.value }))} disabled={celcoinCredentialsLoading} />
                                                <Form.Text className="text-muted payment-mgmt__field-hint">
                                                    Painel Celcoin: Módulos → Webservice → Instalar. Necessário para cobrar assinaturas no cartão: é ele que criptografa o cartão no navegador. Não é secreto.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={12} className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="celcoin-sandbox-inline"
                                                label="Sandbox (ambiente de testes do cel.cash)"
                                                checked={celcoinCredentials.sandbox}
                                                onChange={e => setCelcoinCredentials(p => ({ ...p, sandbox: e.target.checked }))}
                                                disabled={celcoinCredentialsLoading}
                                            />
                                        </Col>
                                        <Col md={12} className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="celcoin-auto-full-sync"
                                                label="Sincronização automática (descoberta de clientes/planos/assinaturas, de hora em hora)"
                                                checked={celcoinCredentials.autoFullSyncEnabled}
                                                onChange={e => setCelcoinCredentials(p => ({ ...p, autoFullSyncEnabled: e.target.checked }))}
                                                disabled={celcoinCredentialsLoading}
                                            />
                                        </Col>
                                    </Row>
                                    <div className="d-flex justify-content-end">
                                        <Button variant="primary" onClick={handleSaveCelcoin} disabled={celcoinCredentialsLoading}>
                                            <i className="fas fa-save me-1"></i>Salvar Celcoin
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            )}

            {currentProvider === 'ASAAS' && (
                <Col md={12} className="mt-4">
                    <Card className="integration-settings__card">
                        <Card.Header className="integration-settings__header">
                            <div>
                                <h5>
                                    <i className="fas fa-sync-alt me-2"></i>
                                    Sincronização Asaas
                                </h5>
                            </div>
                        </Card.Header>
                        <Card.Body className="integration-settings__body">
                            <Alert variant="info">
                                <i className="fas fa-info-circle me-2"></i>
                                Importa clientes, assinaturas e cria/atualiza planos locais inferidos a partir das assinaturas recorrentes do Asaas.
                            </Alert>

                            {asaasSyncResult && (
                                <Alert variant="success">
                                    Clientes: {asaasSyncResult.customersCreated || 0} criados, {asaasSyncResult.customersUpdated || 0} atualizados. Planos: {asaasSyncResult.plansCreated || 0} criados, {asaasSyncResult.plansUpdated || 0} atualizados. Assinaturas: {asaasSyncResult.subscriptionsCreated || 0} criadas, {asaasSyncResult.subscriptionsUpdated || 0} atualizadas.
                                </Alert>
                            )}

                            {asaasPreview && (() => {
                                const dupCustomers = (asaasPreview.customers || []).filter(c => c.possibleDuplicate);
                                return (
                                    <Alert variant={dupCustomers.length > 0 ? 'warning' : 'info'}>
                                        <div className="fw-bold mb-2">
                                            <i className="fas fa-eye me-2"></i>
                                            Prévia (nada foi gravado ainda)
                                        </div>
                                        <div className="mb-2">
                                            Clientes: <b>{asaasPreview.customersCreated || 0}</b> serão criados, {asaasPreview.customersUpdated || 0} atualizados.
                                            Planos: <b>{asaasPreview.plansCreated || 0}</b> criados, {asaasPreview.plansUpdated || 0} atualizados.
                                            Assinaturas: <b>{asaasPreview.subscriptionsCreated || 0}</b> criadas, {asaasPreview.subscriptionsUpdated || 0} atualizadas.
                                        </div>

                                        {dupCustomers.length > 0 ? (
                                            <>
                                                <div className="text-danger fw-bold mb-1">
                                                    <i className="fas fa-triangle-exclamation me-2"></i>
                                                    {dupCustomers.length} cliente(s) sem CPF podem já existir no Colmeia (possível duplicata). Confira antes de confirmar:
                                                </div>
                                                <Table size="sm" bordered className="mb-0 bg-white">
                                                    <thead>
                                                        <tr><th>Cliente</th><th>ID Asaas</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {dupCustomers.map((c, i) => (
                                                            <tr key={i}>
                                                                <td>{c.name}</td>
                                                                <td><code className="payment-mgmt__inline-code">{c.asaasId}</code></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </>
                                        ) : (
                                            <div className="text-success">
                                                <i className="fas fa-check me-2"></i>
                                                Nenhum candidato a duplicata detectado.
                                            </div>
                                        )}

                                        <Button
                                            variant="link"
                                            className="px-0 mt-2"
                                            onClick={() => setShowPreviewDetails(v => !v)}
                                        >
                                            <i className={`fas ${showPreviewDetails ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`}></i>
                                            {showPreviewDetails ? 'Ocultar dados' : 'Ver dados'}
                                        </Button>
                                        {showPreviewDetails && renderPreviewData(asaasPreview, 'asaasId')}
                                    </Alert>
                                );
                            })()}

                            {renderSyncProgress('ASAAS')}

                            {renderScopeSelector()}

                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <div className="text-muted">
                                    <small>
                                        Ultima sincronização: {lastSyncDates.subscriptions
                                            ? new Date(lastSyncDates.subscriptions + 'T00:00:00').toLocaleDateString('pt-BR')
                                            : 'Nunca'}
                                    </small>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        variant="outline-primary"
                                        onClick={handlePreviewAsaas}
                                        disabled={loading || previewLoading || !anyScopeSelected}
                                    >
                                        <i className={`fas ${previewLoading ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>
                                        {previewLoading ? 'Analisando...' : 'Pré-visualizar'}
                                    </Button>
                                    <Button
                                        variant={asaasPreview ? 'success' : 'primary'}
                                        onClick={handleSyncAsaas}
                                        disabled={loading || previewLoading || !anyScopeSelected}
                                    >
                                        <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
                                        {loading ? 'Sincronizando...' : (asaasPreview ? 'Confirmar sincronização' : 'Sincronizar Asaas')}
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            )}

            {currentProvider === 'CELCOIN' && (
                <Col md={12} className="mt-4">
                    <Card className="integration-settings__card">
                        <Card.Header className="integration-settings__header">
                            <div>
                                <h5>
                                    <i className="fas fa-sync-alt me-2"></i>
                                    Sincronização Celcoin
                                </h5>
                            </div>
                        </Card.Header>
                        <Card.Body className="integration-settings__body">
                            <Alert variant="info">
                                <i className="fas fa-info-circle me-2"></i>
                                Importa clientes, assinaturas e cria/atualiza planos locais inferidos a partir das assinaturas recorrentes do Celcoin.
                            </Alert>

                            {celcoinSyncResult && (
                                <Alert variant="success">
                                    Clientes: {celcoinSyncResult.customersCreated || 0} criados, {celcoinSyncResult.customersUpdated || 0} atualizados. Planos: {celcoinSyncResult.plansCreated || 0} criados, {celcoinSyncResult.plansUpdated || 0} atualizados. Assinaturas: {celcoinSyncResult.subscriptionsCreated || 0} criadas, {celcoinSyncResult.subscriptionsUpdated || 0} atualizadas.
                                </Alert>
                            )}

                            {celcoinPreview && (() => {
                                const dupCustomers = (celcoinPreview.customers || []).filter(c => c.possibleDuplicate);
                                return (
                                    <Alert variant={dupCustomers.length > 0 ? 'warning' : 'info'}>
                                        <div className="fw-bold mb-2">
                                            <i className="fas fa-eye me-2"></i>
                                            Prévia (nada foi gravado ainda)
                                        </div>
                                        <div className="mb-2">
                                            Clientes: <b>{celcoinPreview.customersCreated || 0}</b> serão criados, {celcoinPreview.customersUpdated || 0} atualizados.
                                            Planos: <b>{celcoinPreview.plansCreated || 0}</b> criados, {celcoinPreview.plansUpdated || 0} atualizados.
                                            Assinaturas: <b>{celcoinPreview.subscriptionsCreated || 0}</b> criadas, {celcoinPreview.subscriptionsUpdated || 0} atualizadas.
                                        </div>

                                        {dupCustomers.length > 0 ? (
                                            <>
                                                <div className="text-danger fw-bold mb-1">
                                                    <i className="fas fa-triangle-exclamation me-2"></i>
                                                    {dupCustomers.length} cliente(s) sem documento podem já existir no Colmeia (possível duplicata). Confira antes de confirmar:
                                                </div>
                                                <Table size="sm" bordered className="mb-0 bg-white">
                                                    <thead>
                                                        <tr><th>Cliente</th><th>ID Celcoin</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {dupCustomers.map((c, i) => (
                                                            <tr key={i}>
                                                                <td>{c.name}</td>
                                                                <td><code className="payment-mgmt__inline-code">{c.celcoinId}</code></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </>
                                        ) : (
                                            <div className="text-success">
                                                <i className="fas fa-check me-2"></i>
                                                Nenhum candidato a duplicata detectado.
                                            </div>
                                        )}

                                        <Button
                                            variant="link"
                                            className="px-0 mt-2"
                                            onClick={() => setShowPreviewDetails(v => !v)}
                                        >
                                            <i className={`fas ${showPreviewDetails ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`}></i>
                                            {showPreviewDetails ? 'Ocultar dados' : 'Ver dados'}
                                        </Button>
                                        {showPreviewDetails && renderPreviewData(celcoinPreview, 'celcoinId')}
                                    </Alert>
                                );
                            })()}

                            {renderSyncProgress('CELCOIN')}

                            {renderScopeSelector()}

                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <div className="text-muted">
                                    <small>
                                        Ultima sincronização: {lastSyncDates.subscriptions
                                            ? new Date(lastSyncDates.subscriptions + 'T00:00:00').toLocaleDateString('pt-BR')
                                            : 'Nunca'}
                                    </small>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        variant="outline-primary"
                                        onClick={handlePreviewCelcoin}
                                        disabled={loading || previewLoading || !anyScopeSelected}
                                    >
                                        <i className={`fas ${previewLoading ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>
                                        {previewLoading ? 'Analisando...' : 'Pré-visualizar'}
                                    </Button>
                                    <Button
                                        variant={celcoinPreview ? 'success' : 'primary'}
                                        onClick={handleSyncCelcoin}
                                        disabled={loading || previewLoading || !anyScopeSelected}
                                    >
                                        <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
                                        {loading ? 'Sincronizando...' : (celcoinPreview ? 'Confirmar sincronização' : 'Sincronizar Celcoin')}
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            )}

        </Row>
    );


    return (
        <>
            <PageTitle
                activeMenu={t('payment_provider')}
                motherMenu={t('administration')}
            />

            {/* Seletor de empresa */}
            <Row className="mb-4">
                <Col xl={12}>
                    <Card className="integration-settings__card">
                        <Card.Header className="integration-settings__header">
                            <div>
                                <h4 className="mb-0">
                                    <i className="fas fa-store me-2 text-primary"></i>
                                    Empresa
                                </h4>
                                <p className="mb-0 mt-1 text-muted" style={{ fontSize: '0.875rem' }}>
                                    Selecione a empresa para gerenciar as configurações de pagamento.
                                </p>
                            </div>
                        </Card.Header>
                        <Card.Body className="integration-settings__body">
                            <Col md={5}>
                                <CustomSelect
                                    value={companies.find(c => c.value === selectedCompanyId) || null}
                                    onChange={(opt) => setSelectedCompanyId(opt ? opt.value : null)}
                                    options={companies}
                                    placeholder="Selecione uma empresa..."
                                    isClearable
                                    isLoading={companiesLoading}
                                    isDisabled={companiesLoading}
                                />
                            </Col>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {renderJobHistory()}

            {!selectedCompanyId && (
                <Row className="mb-4">
                    <Col xl={12}>
                        <Card className="integration-settings__card">
                            <Card.Body className="text-center py-5">
                                <div className="mb-3">
                                    <i className="fas fa-building" style={{ fontSize: '3.5rem', color: '#dee2e6' }}></i>
                                </div>
                                <h5 className="text-muted mb-2">Nenhuma empresa selecionada</h5>
                                <p className="text-muted mb-0" style={{ maxWidth: 400, margin: '0 auto' }}>
                                    Escolha uma empresa no seletor acima para carregar as configurações de pagamento.
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {selectedCompanyId && (
            <Row>
                <Col xl={12}>
                    <Card className="integration-settings__card payment-mgmt__shell">
                        <Card.Header className="integration-settings__header payment-mgmt__shell-header">
                            <div>
                                <h4>{t('payment_provider')}</h4>
                                <p>Configure o provedor ativo e gerencie planos, clientes e assinaturas sincronizados.</p>
                            </div>
                        </Card.Header>
                        <Card.Body className="integration-settings__body">
                            {loading && !syncJob ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('loading')}...</span>
                                    </div>
                                    <p className="mt-3 text-muted">{t('loading')}...</p>
                                </div>
                            ) : renderOverview()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            )}

            <ModalPageEvent
                title={confirmAction?.title}
                show={showConfirmModal}
                setShow={setShowConfirmModal}
                centered
                btnCloseText="Cancelar"
                btnOnClick={confirmAction?.onConfirm}
                btnDisabled={loading}
                btnText={loading ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processando...
                    </>
                ) : (
                    'Confirmar'
                )}
                child={confirmAction?.message}
            />
        </>
    );
};

export default PaymentProviderManagement;
